import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface QueueItem {
  id: string; code: string;
  vehicle_code: string | null; vehicle_plate: string | null;
  event_type: string; total_cost_sar: string; reported_at: string;
  work_completed_at: string | null;
  reporter_name: string | null;
  status: string;
}

const fmtSar = (s: string) => 'SAR ' + Number(s ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

interface QueueProps {
  title: string;
  filterStatus: string;
}

export function MaintenanceQueuePage({ title, filterStatus }: QueueProps) {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<QueueItem[]>(`/api/v1/maintenance/events?status=${filterStatus}`);
      setItems(Array.isArray(r.data) ? r.data : []);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/maintenance"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Maintenance</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> {title}</div>
          <div className="w-24" />
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 py-4 space-y-3">
        {loading ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        : error ? <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
        : items.length === 0 ? <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">Queue is empty.</div>
        : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((it) => (
              <Link key={it.id} to={`/dashboard/maintenance/${it.id}`}
                className="block rounded-lg border border-border p-3 hover:bg-muted/30 transition-colors space-y-2">
                <div className="flex items-baseline justify-between">
                  <span className="font-mono text-xs">{it.code}</span>
                  <span className="font-mono text-sm">{fmtSar(it.total_cost_sar)}</span>
                </div>
                <div className="text-sm">{it.vehicle_code} · {it.vehicle_plate}</div>
                <div className="text-xs text-muted-foreground">{it.event_type.replace(/_/g, ' ')}</div>
                <div className="text-xs text-muted-foreground">Reported {new Date(it.reported_at).toLocaleDateString('en-GB')} by {it.reporter_name ?? '—'}</div>
                {it.work_completed_at && <div className="text-xs text-muted-foreground">Completed {new Date(it.work_completed_at).toLocaleDateString('en-GB')}</div>}
              </Link>
            ))}
          </div>}
      </main>
    </div>
  );
}

export function PendingVerificationPage() { return <MaintenanceQueuePage title="Pending verification" filterStatus="pending_verification" />; }
export function PendingApprovalPage()     { return <MaintenanceQueuePage title="Pending approval"     filterStatus="verified" />; }
export function PendingPaymentPage()      { return <MaintenanceQueuePage title="Pending payment"      filterStatus="approved_for_payment" />; }
